import React, { useState, useRef, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Button, Segmented, Modal, Tag } from 'antd';
import { LeftOutlined, RightOutlined, CalendarOutlined } from '@ant-design/icons';
import moment from 'moment-jalaali';
import 'moment-jalaali';

const HolidaysCalendarView = ({
  holidays = [],
  deptOptions = [],
  designationOptions = [],
  employmentLabels = {},
}) => {
  const calendarRef = useRef(null);
  const [currentDate, setCurrentDate] = useState(moment());
  const [viewType, setViewType] = useState('month');
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  // محاسبه بازه ۶ هفته‌ای (۴۲ روزه) بر اساس ماه شمسی – مثل dayGridMonth ولی شمسی
  const visibleRange = useMemo(() => {
    if (viewType === 'day') {
      return {
        start: currentDate.clone().format('YYYY-MM-DD'),
        end: currentDate.clone().add(1, 'day').format('YYYY-MM-DD'),
      };
    }
    if (viewType === 'week') {
      const day = currentDate.day();
      const daysFromSat = day === 6 ? 0 : day + 1;
      const startOfWeek = currentDate.clone().subtract(daysFromSat, 'days');
      return {
        start: startOfWeek.format('YYYY-MM-DD'),
        end: startOfWeek.clone().add(7, 'days').format('YYYY-MM-DD'),
      };
    }
    // month / schedule: شروع از شنبه قبل یا روی اول ماه شمسی، ۶ هفته کامل
    const jMonthStart = currentDate.clone().startOf('jMonth');
    const startDay = jMonthStart.day(); // 0=Sun … 6=Sat
    const daysFromSat = startDay === 6 ? 0 : startDay + 1;
    const gridStart = jMonthStart.clone().subtract(daysFromSat, 'days');
    const gridEnd = gridStart.clone().add(42, 'days');
    return {
      start: gridStart.format('YYYY-MM-DD'),
      end: gridEnd.format('YYYY-MM-DD'),
    };
  }, [currentDate, viewType]);

  // ---- ناوبری ----
  const handlePrev = () => {
    setCurrentDate((prev) => {
      if (viewType === 'day') return prev.clone().subtract(1, 'day');
      if (viewType === 'week') return prev.clone().subtract(1, 'week');
      return prev.clone().subtract(1, 'jMonth');
    });
  };

  const handleNext = () => {
    setCurrentDate((prev) => {
      if (viewType === 'day') return prev.clone().add(1, 'day');
      if (viewType === 'week') return prev.clone().add(1, 'week');
      return prev.clone().add(1, 'jMonth');
    });
  };

  const handleToday = () => {
    setCurrentDate(moment());
  };

  const handleViewChange = (value) => {
    setViewType(value);
  };

  const getHeaderTitle = () => {
    if (viewType === 'day') return currentDate.format('jD jMMMM jYYYY');
    if (viewType === 'week') {
      const day = currentDate.day();
      const daysFromSat = day === 6 ? 0 : day + 1;
      const startOfWeek = currentDate.clone().subtract(daysFromSat, 'days');
      const endOfWeek = startOfWeek.clone().add(6, 'days');
      return `${startOfWeek.format('jD jMMMM')} - ${endOfWeek.format('jD jMMMM jYYYY')}`;
    }
    return currentDate.format('jMMMM jYYYY');
  };

  // ---- رویدادها ----
  const events = useMemo(() => {
    return (holidays || []).map((holiday) => {
      const start = holiday.date ? moment(holiday.date).format('YYYY-MM-DD') : undefined;
      return {
        id: String(holiday.id),
        title: holiday.occassion || holiday.occasion || '',
        start,
        backgroundColor: '#ff4d4f',
        borderColor: '#ff4d4f',
        extendedProps: { holidayData: holiday },
      };
    });
  }, [holidays]);

  const handleEventClick = (info) => {
    const holiday = info.event.extendedProps.holidayData;
    if (holiday) {
      setSelectedHoliday(holiday);
      setModalVisible(true);
    }
  };

  // ---- رندر سلول‌ها ----
  const formatDayCell = (dayInfo) => {
    const jDate = moment(dayInfo.date);
    return jDate.format('jD');
  };

  const formatDayHeader = (dayInfo) => {
    const dayNames = ['یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه', 'شنبه'];
    return dayNames[dayInfo.day];
  };

  // کلاس‌های CSS برای روزهای خارج از ماه شمسی (مثل fc-day-other در dayGridMonth)
  const dayCellClassNames = (dayInfo) => {
    if (viewType !== 'month' && viewType !== 'schedule') return [];
    const jDate = moment(dayInfo.date);
    if (jDate.jMonth() !== currentDate.jMonth() || jDate.jYear() !== currentDate.jYear()) {
      return ['fc-day-other-jalali'];
    }
    return [];
  };

  // ---- توابع کمکی مودال ----
  const parseArrayField = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const getDeptName = (id) => {
    const numId = Number(id);
    const dept = deptOptions.find((d) => d.id === numId || d.id === id);
    return dept ? dept.team_name || dept.name : `#${id}`;
  };

  const getDesignationName = (id) => {
    const numId = Number(id);
    const des = designationOptions.find((d) => d.id === numId || d.id === id);
    return des ? des.name : `#${id}`;
  };

  const getEmploymentLabel = (type) => {
    return employmentLabels[type] || type;
  };

  const toJalaliDate = (dateStr) => {
    const m = moment(dateStr);
    if (!m.isValid()) return dateStr || '-';
    return m.format('jYYYY/jMM/jDD');
  };

  const getWeekday = (dateStr) => {
    const m = moment(dateStr);
    if (!m.isValid()) return '-';
    const dayNames = ['یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه', 'شنبه'];
    return dayNames[m.day()];
  };

  return (
    <div className="max-w-7xl mx-auto" style={{ fontFamily: 'CustomFont' }}>
      {/* کنترل‌های ناوبری */}
      <div className="mb-4 flex items-center justify-between flex-wrap gap-4 max-w-full">
        <div className="flex items-center gap-2">
          <Button onClick={handleToday} style={{ fontFamily: 'CustomFont' }}>
            امروز
          </Button>
          <Button icon={<LeftOutlined />} onClick={handlePrev} />
          <Button icon={<RightOutlined />} onClick={handleNext} />
          <div
            className="text-xl font-bold text-gray-800 dark:text-gray-200"
            style={{ fontFamily: 'CustomFont' }}
          >
            {getHeaderTitle()}
          </div>
        </div>

        <Segmented
          value={viewType}
          onChange={handleViewChange}
          options={[
            { label: 'برنامه', value: 'schedule' },
            { label: 'روز', value: 'day' },
            { label: 'هفته', value: 'week' },
            { label: 'ماه', value: 'month' },
          ]}
          style={{ fontFamily: 'CustomFont' }}
        />
      </div>

      {/* تقویم */}
      <div className="calendar-container max-w-full" dir="rtl">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGrid"
          visibleRange={visibleRange}
          headerToolbar={false}
          events={events}
          height="auto"
          firstDay={6}
          eventDisplay="block"
          dayCellContent={formatDayCell}
          dayHeaderContent={formatDayHeader}
          dayCellClassNames={dayCellClassNames}
          locale="en"
          direction="rtl"
          eventClick={handleEventClick}
        />
      </div>

      {/* مودال جزئیات تعطیلی */}
      <Modal
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedHoliday(null);
        }}
        title={
          <div className="flex items-center gap-2" style={{ fontFamily: 'CustomFont' }}>
            <CalendarOutlined className="text-red-500" />
            <span>جزئیات تعطیلی</span>
          </div>
        }
        footer={null}
        centered
        width={480}
      >
        {selectedHoliday && (
          <div style={{ fontFamily: 'CustomFont' }}>
            {/* مناسبت */}
            <div
              className="rounded-lg p-4 text-center mb-4"
              style={{ backgroundColor: '#fff1f0', border: '1px solid #ffa39e' }}
            >
              <div className="text-lg font-bold" style={{ color: '#cf1322' }}>
                {selectedHoliday.occassion || selectedHoliday.occasion || '-'}
              </div>
            </div>

            {/* تاریخ و روز هفته */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="rounded-lg p-3" style={{ backgroundColor: '#f5f5f5' }}>
                <div className="text-xs mb-1" style={{ color: '#8c8c8c' }}>
                  تاریخ شمسی
                </div>
                <div className="font-bold" style={{ color: '#262626' }}>
                  {toJalaliDate(selectedHoliday.date)}
                </div>
              </div>
              <div className="rounded-lg p-3" style={{ backgroundColor: '#f5f5f5' }}>
                <div className="text-xs mb-1" style={{ color: '#8c8c8c' }}>
                  روز هفته
                </div>
                <div className="font-bold" style={{ color: '#262626' }}>
                  {getWeekday(selectedHoliday.date)}
                </div>
              </div>
            </div>

            {/* دپارتمان‌ها */}
            {parseArrayField(selectedHoliday.department_id_json).length > 0 && (
              <div className="mb-3">
                <div className="text-sm mb-2" style={{ color: '#8c8c8c' }}>
                  دپارتمان‌ها
                </div>
                <div className="flex flex-wrap gap-1">
                  {parseArrayField(selectedHoliday.department_id_json).map((id) => (
                    <Tag key={id} color="blue">
                      {getDeptName(id)}
                    </Tag>
                  ))}
                </div>
              </div>
            )}

            {/* سمت‌ها */}
            {parseArrayField(selectedHoliday.designation_id_json).length > 0 && (
              <div className="mb-3">
                <div className="text-sm mb-2" style={{ color: '#8c8c8c' }}>
                  سمت‌ها
                </div>
                <div className="flex flex-wrap gap-1">
                  {parseArrayField(selectedHoliday.designation_id_json).map((id) => (
                    <Tag key={id} color="green">
                      {getDesignationName(id)}
                    </Tag>
                  ))}
                </div>
              </div>
            )}

            {/* نوع استخدام */}
            {parseArrayField(selectedHoliday.employment_type_json).length > 0 && (
              <div className="mb-3">
                <div className="text-sm mb-2" style={{ color: '#8c8c8c' }}>
                  نوع استخدام
                </div>
                <div className="flex flex-wrap gap-1">
                  {parseArrayField(selectedHoliday.employment_type_json).map((type) => (
                    <Tag key={type} color="orange">
                      {getEmploymentLabel(type)}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <style>{`
        .calendar-container {
          direction: rtl;
          max-width: 100%;
          overflow-x: auto;
        }
        .calendar-container .fc {
          direction: rtl !important;
          font-family: 'CustomFont', 'Vazir', 'Tahoma', sans-serif;
          max-width: 100%;
        }
        .calendar-container .fc-scrollgrid {
          max-width: 100%;
          table-layout: fixed;
        }
        .calendar-container .fc-col-header-cell {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          text-align: center;
          font-weight: bold;
          padding: 8px;
          font-family: 'CustomFont', 'Vazir', 'Tahoma', sans-serif;
        }
        .calendar-container .fc-daygrid-day {
          min-width: 0;
          overflow: hidden;
        }
        .calendar-container .fc-daygrid-day-number {
          text-align: right;
          padding: 8px;
          font-family: 'CustomFont', 'Vazir', 'Tahoma', sans-serif;
        }
        .calendar-container .fc-day-today {
          background-color: rgba(24, 144, 255, 0.1) !important;
        }
        .calendar-container .fc-daygrid-day-frame {
          min-height: 80px;
        }
        @media (min-width: 1280px) {
          .calendar-container .fc-daygrid-day-frame {
            min-height: 100px;
          }
        }
        /* استایل روزهای خارج ماه شمسی – شبیه fc-day-other در dayGridMonth */
        .calendar-container .fc-day-other-jalali {
          background-color: rgba(208, 208, 208, 0.15) !important;
        }
        .calendar-container .fc-day-other-jalali .fc-daygrid-day-top {
          opacity: 0.35;
        }
        .calendar-container .fc-day-other-jalali .fc-daygrid-day-events {
          opacity: 0.5;
        }
        /* استایل کلیک‌پذیر رویدادها */
        .calendar-container .fc-event {
          cursor: pointer !important;
          transition: opacity 0.2s;
        }
        .calendar-container .fc-event:hover {
          opacity: 0.85;
        }
      `}</style>
    </div>
  );
};

export default HolidaysCalendarView;
